import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSocket } from '../../contexts/SocketContext';
import apiService from '../../services/api';
import toast from 'react-hot-toast';
import { 
  Bars3Icon, 
  XMarkIcon, 
  UserCircleIcon,
  ChevronDownIcon,
  ChatBubbleLeftEllipsisIcon,
  BellIcon,
  MagnifyingGlassIcon,
  UserGroupIcon,
  UsersIcon
} from '@heroicons/react/24/outline';
import Button from '../ui/Button';

const Header: React.FC = () => {
  const { user, isAuthenticated, logout, isLoading } = useAuth();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isHostMenuOpen, setIsHostMenuOpen] = useState(false);
  const [messageCount, setMessageCount] = useState(0);
  const [notificationCount, setNotificationCount] = useState(0);
  const { socket, isConnected } = useSocket();

  // Debug logging
  console.log('Header Debug:', { 
    isAuthenticated, 
    user, 
    isLoading,
    userFirstName: user?.firstName,
    userEmail: user?.email 
  });

  // Function to show toast notification using react-hot-toast
  const showToast = (message: string, type: 'success' | 'error' | 'warning' = 'warning') => {
    switch (type) {
      case 'success':
        toast.success(message);
        break;
      case 'error':
        toast.error(message);
        break;
      case 'warning':
      default:
        toast.error(message, {
          icon: '⚠️',
        });
        break;
    }
  };

  // Authentication check handler for host/promote actions
  const handleAuthenticatedAction = (path: string, actionName: string) => {
    if (!isAuthenticated) {
      showToast('You are not logged in. Please login to access this feature.', 'warning');
      
      // Redirect to login page after 2 seconds
      setTimeout(() => {
        navigate(`/login?redirect=${encodeURIComponent(path)}`);
      }, 2000);
      
      return false;
    }
    
    // User is authenticated, navigate to the requested page
    navigate(path);
    setIsHostMenuOpen(false);
    setIsMobileMenuOpen(false);
    return true;
  };

  // Fetch initial counts
  useEffect(() => {
    const fetchCounts = async () => {
      if (!isAuthenticated || !user) {
        console.log('Header: User not authenticated, resetting counts');
        setMessageCount(0);
        setNotificationCount(0);
        return;
      }
      
      console.log('Header: Fetching counts for user:', user._id);
      try {
        console.log('Header: Making API call to get unread count...');
        const unreadRes = await apiService.getUnreadMessageCounts();
        console.log('Header: API response:', unreadRes);
        
        if (unreadRes.success && unreadRes.data) {
          const totalCount = Object.values(unreadRes.data).reduce((sum, count) => sum + count, 0);
          console.log('Header: Unread message count (new format):', totalCount);
          setMessageCount(totalCount);
        } else {
          // Fallback to old endpoint
          const oldUnreadRes = await apiService.getUnreadMessageCount();
          const count = oldUnreadRes.success && oldUnreadRes.data ? oldUnreadRes.data.count : 0;
          console.log('Header: Unread message count (old format):', count);
          setMessageCount(count);
        }
      } catch (error: any) {
        console.error('Header: Full error details:', error);
        if (error?.response?.status === 401) {
          console.log('Header: 401 error - user not authenticated');
          setMessageCount(0); // Not logged in, so no unread messages
        } else {
          console.error('Error fetching unread message count:', error);
        }
      }
      try {
        const notifRes = await apiService.getUserNotifications();
        const notifCount = notifRes.success && Array.isArray(notifRes.data) ? notifRes.data.filter((n: any) => !n.read).length : 0;
        console.log('Header: Notification count:', notifCount);
        setNotificationCount(notifCount);
      } catch (error: any) {
        if (error?.response?.status === 401) {
          setNotificationCount(0); // Not logged in, so no notifications
        } else {
          console.error('Error fetching notifications:', error);
        }
      }
    };
    
    // Fetch counts immediately
    fetchCounts();
    
    // Also fetch counts after a short delay to handle timing issues
    const timeoutId = setTimeout(fetchCounts, 1000);
    
    return () => clearTimeout(timeoutId);
  }, [isAuthenticated, user]);

  // Real-time updates
  useEffect(() => {
    if (!socket || !isConnected) return;
    
    const handleUnreadCountUpdate = (data: { unreadCounts?: { [userId: string]: number }; count?: number }) => {
      console.log('Header: Received unread count update:', data);
      
      // Handle new format with unreadCounts
      if (data.unreadCounts) {
        const totalCount = Object.values(data.unreadCounts).reduce((sum, count) => sum + count, 0);
        console.log('Header: Calculated total count from unreadCounts:', totalCount);
        setMessageCount(totalCount);
      }
      // Handle old format with count (for backward compatibility)
      else if (data.count !== undefined) {
        console.log('Header: Using count from data:', data.count);
        setMessageCount(data.count);
      }
    };
    
    const handleNewNotification = () => {
      console.log('Header: Received new notification');
      setNotificationCount((c) => c + 1);
    };
    
    // Refetch counts when socket connects or reconnects
    const fetchCountsOnReconnect = async () => {
      if (isAuthenticated && user) {
        try {
          const unreadRes = await apiService.getUnreadMessageCounts();
          if (unreadRes.success && unreadRes.data) {
            const totalCount = Object.values(unreadRes.data).reduce((sum, count) => sum + count, 0);
            setMessageCount(totalCount);
          } else {
            // Fallback to old endpoint
            const oldUnreadRes = await apiService.getUnreadMessageCount();
            setMessageCount(oldUnreadRes.success && oldUnreadRes.data ? oldUnreadRes.data.count : 0);
          }
        } catch (error) {
          console.error('Error fetching unread count on reconnect:', error);
        }
      }
    };
    
    socket.on('unread_count_update', handleUnreadCountUpdate);
    socket.on('new_notification', handleNewNotification);
    socket.on('connect', fetchCountsOnReconnect);
    socket.on('reconnect', fetchCountsOnReconnect);
    
    // If socket is already connected, fetch counts immediately
    if (socket.connected) {
      fetchCountsOnReconnect();
    }
    
    return () => {
      socket.off('unread_count_update', handleUnreadCountUpdate);
      socket.off('new_notification', handleNewNotification);
      socket.off('connect', fetchCountsOnReconnect);
      socket.off('reconnect', fetchCountsOnReconnect);
    };
  }, [socket, isConnected, isAuthenticated, user]);

  // Reset counts when user logs out
  useEffect(() => {
    if (!isAuthenticated) {
      setMessageCount(0);
      setNotificationCount(0);
    }
  }, [isAuthenticated]);

  // Reset counts when viewing
  const handleViewMessages = () => {
    // Don't reset message count here - it will be updated via Socket.IO when messages are marked as read
  };
  const handleViewNotifications = async () => {
    try {
      await apiService.markAllNotificationsAsRead();
      setNotificationCount(0);
    } catch (error) {
      console.error('Error marking notifications as read:', error);
    }
  };

  const navigation = [
    { name: 'Find Team', href: '/search-teams' },
    { name: 'Events', href: '/events' },
    { name: 'Hackathons', href: '/hackathons' },
    { name: 'Jobs', href: '/jobs' },
    { name: 'Quizzes', href: '/quizzes' },
  ];

  const authenticatedNavigation = [
    { name: '', href: '/search-teams' }
  ];

  const handleLogout = async () => {
    try {
      // Call the logout API endpoint using apiService method
      await apiService.logout();
      toast.success('Logged out successfully');
    } catch (error: any) {
      console.error('Logout API error:', error);
      // Even if API fails, continue with client-side logout
      toast.error('Logout completed');
    }
    
    // Always perform client-side logout regardless of API response
    await logout();
    navigate('/');
    setIsUserMenuOpen(false);
  };

  const handleSearchClick = () => {
    navigate('/search');
  };

  // Manual refresh function for debugging
  const manualRefreshCounts = async () => {
    console.log('Header: Manual refresh triggered');
    if (!isAuthenticated || !user) {
      console.log('Header: Cannot refresh - user not authenticated');
      return;
    }
    
    try {
      // Try the new unread counts endpoint first
      const unreadRes = await apiService.getUnreadMessageCounts();
      console.log('Header: Manual refresh response (new format):', unreadRes);
      
      if (unreadRes.success && unreadRes.data) {
        const totalCount = Object.values(unreadRes.data).reduce((sum, count) => sum + count, 0);
        console.log('Header: Manual refresh total count:', totalCount);
        setMessageCount(totalCount);
      } else {
        // Fallback to old endpoint
        const oldUnreadRes = await apiService.getUnreadMessageCount();
        console.log('Header: Manual refresh response (old format):', oldUnreadRes);
        const count = oldUnreadRes.success && oldUnreadRes.data ? oldUnreadRes.data.count : 0;
        console.log('Header: Manual refresh count (old format):', count);
        setMessageCount(count);
      }
    } catch (error) {
      console.error('Header: Manual refresh error:', error);
    }
  };

  // Expose manual refresh function globally for debugging
  React.useEffect(() => {
    (window as any).refreshHeaderCounts = manualRefreshCounts;
    return () => {
      delete (window as any).refreshHeaderCounts;
    };
  }, [isAuthenticated, user]);

  return (
    <header className="bg-white shadow-sm border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div className="flex items-center flex-shrink-0">
            <Link to="/" className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-gradient-to-r from-primary-600 to-purple-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">CS</span>
              </div>
              <span className="text-xl font-bold text-gradient hidden sm:block">CodeSpirit</span>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex space-x-4 ml-12">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className="text-gray-700 hover:text-primary-600 px-3 py-2 text-sm font-medium transition-colors duration-200"
              >
                {item.name}
              </Link>
            ))}
          
              <div className="relative">
                <button
                  onClick={() => setIsHostMenuOpen((v) => !v)}
                  className="text-gray-700 hover:text-primary-600 px-3 py-2 text-sm font-medium flex items-center gap-1 transition-colors duration-200"
                >
                  Host & Promote <ChevronDownIcon className="w-4 h-4" />
                </button>
                {isHostMenuOpen && (
                  <div className="absolute left-0 mt-2 w-56 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200">
                    <button
                      onClick={() => handleAuthenticatedAction('/create-hackathon', 'Host or Promote Hackathon')}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      Host or Promote Hackathon
                    </button>
                    <button
                      onClick={() => handleAuthenticatedAction('/create-event', 'Host or Promote Event')}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      Host or Promote Event
                    </button>
                    <button
                      onClick={() => handleAuthenticatedAction('/create-quiz', 'Create Quiz')}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      Create Quiz
                    </button>
                    <Link
                      to="/partner-with-us"
                      onClick={() => setIsHostMenuOpen(false)}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      Partner With Us
                    </Link>
                    <Link
                      to="/organiser"
                      onClick={() => setIsHostMenuOpen(false)}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      Organisers
                    </Link>
                  </div>
                )}
              </div>
        
            {isAuthenticated && authenticatedNavigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className="text-gray-700 hover:text-primary-600 px-3 py-2 text-sm font-medium transition-colors duration-200"
              >
                {item.name}
              </Link>
            ))}
          </nav>

          {/* Search Bar - Desktop & Tablet */}
          {isAuthenticated && (
            <div className="hidden sm:flex flex-1 max-w-lg mx-4 lg:mx-8">
              <div 
                onClick={handleSearchClick}
                className="relative w-full cursor-pointer group"
              >
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 group-hover:text-primary-500 transition-colors duration-200" />
                </div>
                <input
                  type="text"
                  placeholder="Find connections & teams..."
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg leading-5 bg-gray-50 placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 group-hover:bg-white transition-all duration-200 cursor-pointer"
                  readOnly
                />
              </div>
            </div>
          )}

          {/* Desktop Right Side Icons & User Menu */}
          <div className="flex items-center space-x-3">
            {/* Message and Notification Icons */}
            {isAuthenticated && (
              <>
                <Link to="/messages" className="relative group hidden sm:block" onClick={handleViewMessages}>
                  <ChatBubbleLeftEllipsisIcon className="w-6 h-6 text-gray-500 hover:text-primary-600 transition-colors duration-200" />
                  {messageCount > 0 && (
                    <span className="absolute -top-1 -right-2 bg-red-500 text-white text-xs rounded-full px-1.5 py-0.5 font-bold min-w-[20px] text-center">
                      {messageCount > 99 ? '99+' : messageCount}
                    </span>
                  )}
                </Link>
                <Link to="/notifications" className="relative group hidden sm:block" onClick={handleViewNotifications}>
                  <BellIcon className="w-6 h-6 text-gray-500 hover:text-primary-600 transition-colors duration-200" />
                  {notificationCount > 0 && (
                    <span className="absolute -top-1 -right-2 bg-red-500 text-white text-xs rounded-full px-1.5 py-0.5 font-bold min-w-[20px] text-center">
                      {notificationCount > 99 ? '99+' : notificationCount}
                    </span>
                  )}
                </Link>
              </>
            )}

            {/* Mobile Icons - Search, Messages, Notifications (when authenticated) */}
            {isAuthenticated && (
              <div className="flex items-center space-x-2 sm:hidden">
                {/* Mobile Search Icon */}
                <button
                  onClick={handleSearchClick}
                  className="p-2 text-gray-500 hover:text-primary-600 transition-colors duration-200"
                >
                  <MagnifyingGlassIcon className="w-6 h-6" />
                </button>

                {/* Mobile Messages Icon */}
                <Link
                  to="/messages"
                  className="relative p-2 text-gray-500 hover:text-primary-600 transition-colors duration-200"
                  onClick={handleViewMessages}
                >
                  <ChatBubbleLeftEllipsisIcon className="w-6 h-6" />
                  {messageCount > 0 && (
                    <span className="absolute top-0 right-0 bg-red-500 text-white text-xs rounded-full px-1.5 py-0.5 font-bold min-w-[18px] text-center">
                      {messageCount > 99 ? '99+' : messageCount}
                    </span>
                  )}
                </Link>

                {/* Mobile Notifications Icon */}
                <Link
                  to="/notifications"
                  className="relative p-2 text-gray-500 hover:text-primary-600 transition-colors duration-200"
                  onClick={handleViewNotifications}
                >
                  <BellIcon className="w-6 h-6" />
                  {notificationCount > 0 && (
                    <span className="absolute top-0 right-0 bg-red-500 text-white text-xs rounded-full px-1.5 py-0.5 font-bold min-w-[18px] text-center">
                      {notificationCount > 99 ? '99+' : notificationCount}
                    </span>
                  )}
                </Link>
              </div>
            )}

            {/* User Menu or Auth Buttons */}
            {isAuthenticated ? (
              <div className="relative">
                <button
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  className="flex items-center space-x-2 text-gray-700 hover:text-primary-600 transition-colors duration-200 p-1 rounded-lg hover:bg-gray-50"
                >
                  {user?.profilePicture ? (
                    <img
                      src={user.profilePicture}
                      alt={user.firstName}
                      className="w-8 h-8 rounded-full object-cover"
                    />
                  ) : (
                    <UserCircleIcon className="w-8 h-8" />
                  )}
                  <span className="text-sm font-medium hidden md:block">{user?.firstName}</span>
                  <ChevronDownIcon className="w-4 h-4 hidden md:block" />
                </button>

                {isUserMenuOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200">
                    <Link
                      to="/profile"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      onClick={() => setIsUserMenuOpen(false)}
                    >
                      Profile
                    </Link>
                    <Link
                      to="/user-dashboard"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      onClick={() => setIsUserMenuOpen(false)}
                    >
                      Your Dashboard
                    </Link>
                    <Link
                      to="/myteam"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      onClick={() => setIsUserMenuOpen(false)}
                    >
                      My Team
                    </Link>
                    <Link
                      to="/saved"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      onClick={() => setIsUserMenuOpen(false)}
                    >
                      Saved
                    </Link>
                    <Link
                      to="/dashboard"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      onClick={() => setIsUserMenuOpen(false)}
                    >
                      Organiser's Dashboard
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      Sign out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
                {/* Desktop Auth Buttons */}
                <div className="hidden sm:flex items-center space-x-3">
                  <Link to="/login">
                    <Button variant="outline" size="sm">
                      Sign in
                    </Button>
                  </Link>
                  <Link to="/signup">
                    <Button size="sm">
                      Sign up
                    </Button>
                  </Link>
                </div>
                {/* Mobile Sign in Button */}
                <div className="sm:hidden">
                  <Link to="/login">
                    <Button variant="outline" size="sm" className="text-xs px-3 py-1.5">
                      Sign in
                    </Button>
                  </Link>
                </div>
              </>
            )}

            {/* Mobile menu button */}
            <div className="sm:hidden">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="text-gray-700 hover:text-primary-600 p-2"
              >
                {isMobileMenuOpen ? (
                  <XMarkIcon className="w-6 h-6" />
                ) : (
                  <Bars3Icon className="w-6 h-6" />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isMobileMenuOpen && (
        <div className="sm:hidden">
          <div className="px-2 pt-2 pb-3 space-y-1 bg-white border-t border-gray-200">
            {/* Mobile Search Bar (only in menu when authenticated) */}
            {isAuthenticated && (
              <div className="px-3 py-2">
                <div 
                  onClick={handleSearchClick}
                  className="relative w-full cursor-pointer group"
                >
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 group-active:text-primary-500" />
                  </div>
                  <input
                    type="text"
                    placeholder="Find connections and teams..."
                    className="block w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg bg-gray-50 placeholder-gray-500 text-base cursor-pointer"
                    readOnly
                  />
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <div className="flex space-x-1">
                      <UsersIcon className="h-4 w-4 text-gray-400" />
                      <UserGroupIcon className="h-4 w-4 text-gray-400" />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Mobile Navigation Links */}
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className="text-gray-700 hover:text-primary-600 block px-3 py-2 text-base font-medium"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {item.name}
              </Link>
            ))}

            {/* Mobile Host Menu */}
            <div>
              <button
                onClick={() => setIsHostMenuOpen((v) => !v)}
                className="text-gray-700 hover:text-primary-600 w-full text-left px-3 py-2 text-base font-medium flex items-center justify-between"
              >
                Host & Promote 
                <ChevronDownIcon className={`w-4 h-4 transform transition-transform ${isHostMenuOpen ? 'rotate-180' : ''}`} />
              </button>
              {isHostMenuOpen && (
                <div className="pl-6 space-y-1">
                  <button
                    onClick={() => handleAuthenticatedAction('/create-hackathon', 'Host or Promote Hackathon')}
                    className="block w-full text-left px-3 py-2 text-sm text-gray-600 hover:text-primary-600"
                  >
                    Host or Promote Hackathon
                  </button>
                  <button
                    onClick={() => handleAuthenticatedAction('/create-event', 'Host or Promote Event')}
                    className="block w-full text-left px-3 py-2 text-sm text-gray-600 hover:text-primary-600"
                  >
                    Host or Promote Event
                  </button>
                  <button
                    onClick={() => handleAuthenticatedAction('/create-quiz', 'Create Quiz')}
                    className="block w-full text-left px-3 py-2 text-sm text-gray-600 hover:text-primary-600"
                  >
                    Create Quiz
                  </button>
                  <Link
                    to="/partner-with-us"
                    onClick={() => {
                      setIsHostMenuOpen(false);
                      setIsMobileMenuOpen(false);
                    }}
                    className="block w-full text-left px-3 py-2 text-sm text-gray-600 hover:text-primary-600"
                  >
                    Partner With Us
                  </Link>
                  <Link
                    to="/organiser"
                    onClick={() => {
                      setIsHostMenuOpen(false);
                      setIsMobileMenuOpen(false);
                    }}
                    className="block w-full text-left px-3 py-2 text-sm text-gray-600 hover:text-primary-600"
                  >
                    Organisers
                  </Link>
                </div>
              )}
            </div>

            {/* Mobile Authenticated Navigation */}
            {isAuthenticated && authenticatedNavigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className="text-gray-700 hover:text-primary-600 block px-3 py-2 text-base font-medium"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {item.name}
              </Link>
            ))}

            {/* Mobile User Menu */}
            {isAuthenticated ? (
              <div className="pt-4 border-t border-gray-200">
                <Link
                  to="/profile"
                  className="text-gray-700 hover:text-primary-600 block px-3 py-2 text-base font-medium"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Profile
                </Link>
                <Link
                  to="/myteam"
                  className="text-gray-700 hover:text-primary-600 block px-3 py-2 text-base font-medium"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  My Team
                </Link>
                <Link
                  to="/saved"
                  className="text-gray-700 hover:text-primary-600 block px-3 py-2 text-base font-medium"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Saved
                </Link>
                <Link
                  to="/dashboard"
                  className="text-gray-700 hover:text-primary-600 block px-3 py-2 text-base font-medium"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Organiser's Dashboard
                </Link>
                <button
                  onClick={handleLogout}
                  className="text-gray-700 hover:text-primary-600 block w-full text-left px-3 py-2 text-base font-medium"
                >
                  Sign out
                </button>
              </div>
            ) : (
              <div className="pt-4 border-t border-gray-200 space-y-3 px-3">
                <Link
                  to="/login"
                  className="block w-full text-center px-4 py-3 text-base font-medium text-primary-600 border-2 border-primary-600 rounded-lg hover:bg-primary-50 transition-colors"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Sign in
                </Link>
                <Link
                  to="/signup"
                  className="block w-full text-center px-4 py-3 text-base font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Sign up
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;